import React, { useState, useEffect } from 'react';
import {
    Avatar,
    Typography,
    Button,
    Spin,
    Card,
    Space,
    Tag,
    Breadcrumb,
    App,
    Modal,
    Input,
    Tooltip,
    Row,
    Col
} from 'antd';
import {
    MailOutlined,
    TeamOutlined,
    IdcardOutlined,
    SecurityScanOutlined,
    CopyOutlined,
    KeyOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import api, { userService, securityService } from '../services/api';
import { usePageTitle } from '../context/PageTitleContext';
import ErrorPage from './ErrorPage/ErrorPage';
import HorizontalViewItem from '../components/HorizontalItems/ViewItem';

const { Title, Text } = Typography;
const { TextArea } = Input;

const UserDetails = () => {
    const { setPageTitle, setRightContent } = usePageTitle();
    const navigate = useNavigate();
    const { id } = useParams();
    const { message, modal } = App.useApp();

    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [resetPasswordData, setResetPasswordData] = useState(null);
    const [twoFAData, setTwoFAData] = useState(null);
    const [twoFALoading, setTwoFALoading] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [resetPasswordModalVisible, setResetPasswordModalVisible] = useState(false);

    // Форматирование названия ролей
    const formatRole = (roleName) => {
        return roleName || 'Без роли';
    };

    useEffect(() => {
        setPageTitle('Страница пользователя');
        setRightContent(
            <Button
                type="primary"
                size="large"
                onClick={() => navigate(`/users/${id}/edit`)}
            >
                Редактировать
            </Button>
        );

        return () => {
            setPageTitle('');
            setRightContent(null);
        };
    }, [setPageTitle, setRightContent, id]);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));
        setCurrentUser(user);
    }, []);

    useEffect(() => {
        fetchUserData();
    }, [id]);

    const fetchUserData = async () => {
        try {
            setLoading(true);
            const response = await userService.getUserProfile(id);
            setUserData(response);

            // Получаем информацию о 2FA только если пользователь - админ
            const user = JSON.parse(localStorage.getItem('user'));
            if (user?.role === 'admin') {
                try {
                    setTwoFALoading(true);
                    const twoFAResponse = await securityService.get2FAInfo(id);
                    if (twoFAResponse.status === 'success') {
                        setTwoFAData(twoFAResponse.data);
                    }
                } catch (error) {
                    // Если 2FA не активирован или информация недоступна, просто продолжаем
                } finally {
                    setTwoFALoading(false);
                }
            }
        } catch (error) {
            if (error.response?.status === 404) {
                setError({
                    statusCode: 404,
                    title: "Пользователь не найден",
                    description: "Указанный пользователь не существует или был удален."
                });
            } else {
                setError({
                    statusCode: error.response?.status || 500,
                    title: "Ошибка загрузки данных",
                    description: "Произошла ошибка при загрузке данных пользователя. Попробуйте обновить страницу или вернуться позже."
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleReset2FAClick = () => {
        modal.confirm({
            title: 'Сбросить 2FA пользователя',
            content: 'Вы уверены, что хотите сбросить двухфакторную аутентификацию этого пользователя? Он больше не сможет использовать текущий ключ 2FA.',
            okText: 'Да, сбросить',
            cancelText: 'Отмена',
            okButtonProps: { danger: true },
            onOk: reset2FA,
        });
    };

    const reset2FA = async () => {
        try {
            const response = await securityService.reset2FA(id);
            if (response.status === 'success') {
                message.success(response.message);
                // Обновляем информацию после сброса
                fetchUserData();
            }
        } catch (error) {
            message.error(error.response?.data?.message || 'Ошибка при сбросе 2FA');
        }
    };

    const handleResetPasswordClick = () => {
        modal.confirm({
            title: 'Сбросить пароль пользователя',
            content: 'Вы уверены, что хотите сбросить пароль этого пользователя? Будет сгенерирован новый временный пароль.',
            okText: 'Да, сбросить',
            cancelText: 'Отмена',
            okButtonProps: { danger: true },
            onOk: resetPassword,
        });
    };

    const resetPassword = async () => {
        try {
            const response = await userService.resetUserPassword(id);
            if (response.status === 'success') {
                setResetPasswordData(response.data);
                setResetPasswordModalVisible(true);
            }
        } catch (error) {
            message.error(error.response?.data?.message || 'Ошибка при сбросе пароля');
        }
    };

    const handleCopyText = (text) => {
        navigator.clipboard.writeText(text)
            .then(() => {
                message.success('Скопировано в буфер обмена');
            })
            .catch(() => {
                message.error('Не удалось скопировать текст');
            });
    };

    const getFullCredentialsText = () => {
        if (!resetPasswordData) return '';
        return `Логин: ${resetPasswordData.username}
Временный пароль: ${resetPasswordData.temporary_password}
Текущая почта: ${resetPasswordData.email}
Перед тем как авторизоваться в других сервисах, войдите в сервис ID по временному паролю и смените его на постоянный.`;
    };

    const getInitials = (firstName, lastName) => {
        if (!firstName && !lastName) return 'U';
        return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Не указано';
        return new Date(dateString).toLocaleDateString('ru-RU');
    };

    if (loading) {
        return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
    }

    if (error) {
        return <ErrorPage
            statusCode={error.statusCode}
            title={error.title}
            description={error.description}
        />;
    }

    return (
        <>
            <Breadcrumb
                items={[
                    { title: <a onClick={() => navigate('/users')}>Пользователи</a> },
                    { title: userData?.username || `Пользователь #${id}` }
                ]}
                style={{ marginBottom: 16 }}
            />

            <Card style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Avatar
                        size={80}
                        style={{ backgroundColor: '#1677ff', fontSize: 36, marginRight: 24 }}
                    >
                        {getInitials(userData?.first_name, userData?.last_name)}
                    </Avatar>

                    <div>
                        <Title level={3} style={{ marginTop: 0, marginBottom: 8 }}>
                            {userData?.first_name} {userData?.last_name}
                        </Title>
                        <Text type="secondary">{userData?.username}</Text>
                    </div>
                </div>
            </Card>

            <Row gutter={24}>
                <Col span={12}>
                    {/* Учетные данные */}
                    <Card title="Учетные данные" style={{ marginBottom: 24 }}>
                        <HorizontalViewItem label="ID пользователя">
                            {userData?.id}
                        </HorizontalViewItem>
                        <HorizontalViewItem label="Логин">
                            {userData?.username}
                        </HorizontalViewItem>
                        <HorizontalViewItem label="Email">
                            <Space>
                                <MailOutlined />
                                {userData?.email}
                            </Space>
                        </HorizontalViewItem>
                        <HorizontalViewItem label="Роль">
                            {userData?.role === 'admin' ? 'Администратор' : 'Пользователь'}
                        </HorizontalViewItem>
                        <HorizontalViewItem label="Дата регистрации">
                            {formatDate(userData?.created_at)}
                        </HorizontalViewItem>
                        <HorizontalViewItem label="Последнее обновление">
                            {formatDate(userData?.updated_at)}
                        </HorizontalViewItem>
                    </Card>

                    {/* Личные данные */}
                    <Card title="Личные данные" style={{ marginBottom: 24 }}>
                        <HorizontalViewItem label="Имя">
                            {userData?.first_name || 'Не указано'}
                        </HorizontalViewItem>
                        <HorizontalViewItem label="Фамилия">
                            {userData?.last_name || 'Не указано'}
                        </HorizontalViewItem>
                        <HorizontalViewItem label="Отдел">
                            <Space>
                                <TeamOutlined />
                                {userData?.department || 'Не указан'}
                            </Space>
                        </HorizontalViewItem>
                        <HorizontalViewItem label="Должность">
                            <Space>
                                <IdcardOutlined />
                                {userData?.position || 'Не указана'}
                            </Space>
                        </HorizontalViewItem>
                    </Card>

                    {/* Доступ к складам в WMS */}
                    <Card title="Доступ к складам в WMS" style={{ marginBottom: 24 }}>
                        <HorizontalViewItem label="Доступные склады">
                            {userData?.warehouse_ids?.length > 0 ? (
                                <Space wrap>
                                    {userData.warehouse_ids.map((warehouseId) => (
                                        <Tag key={warehouseId} color="blue">
                                            Склад #{warehouseId}
                                        </Tag>
                                    ))}
                                </Space>
                            ) : (
                                <Text type="secondary">Нет доступа к складам</Text>
                            )}
                        </HorizontalViewItem>
                    </Card>
                </Col>

                <Col span={12}>
                    {/* Настройки доступа */}
                    <Card title="Настройки доступа" style={{ marginBottom: 24 }}>
                        <HorizontalViewItem label="Статус">
                            <Tag color={userData?.is_active ? 'green' : 'red'}>
                                {userData?.is_active ? 'Активен' : 'Неактивен'}
                            </Tag>
                        </HorizontalViewItem>
                        <HorizontalViewItem label="Тип пароля">
                            <Tag color={userData?.is_temporary_password ? 'orange' : 'green'}>
                                {userData?.is_temporary_password ? 'Временный пароль' : 'Постоянный пароль'}
                            </Tag>
                        </HorizontalViewItem>
                        {currentUser?.role === 'admin' && (
                            <HorizontalViewItem label="Статус 2FA">
                                {twoFALoading ? (
                                    <Spin size="small" />
                                ) : twoFAData ? (
                                    <div>
                                        <Tag color="green">Активирован</Tag>
                                        <div style={{ marginTop: 4 }}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                Активирован: {formatDate(twoFAData.activated_at)}
                                            </Text>
                                            <br />
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                Создан: {formatDate(twoFAData.created_at)}
                                            </Text>
                                        </div>
                                    </div>
                                ) : (
                                    <Tag color="default">Не активирован</Tag>
                                )}
                            </HorizontalViewItem>
                        )}

                        <div style={{ marginTop: 24, textAlign: 'center' }}>
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                {currentUser?.role === 'admin' && twoFAData && (
                                    <Button
                                        type="default"
                                        size="middle"
                                        icon={<SecurityScanOutlined />}
                                        onClick={handleReset2FAClick}
                                        danger
                                        block
                                    >
                                        Сбросить 2FA
                                    </Button>
                                )}
                                <Button
                                    type="default"
                                    size="middle"
                                    icon={<KeyOutlined />}
                                    onClick={handleResetPasswordClick}
                                    danger
                                    block
                                >
                                    Сбросить пароль
                                </Button>
                            </Space>
                        </div>
                    </Card>
                    {/* Роли во внешних системах */}
                    <Card title="Роли во внешних системах" style={{ marginBottom: 24 }}>
                        <HorizontalViewItem label="Роль в WMS">
                            {formatRole(userData?.role_wms_name)}
                        </HorizontalViewItem>
                        <HorizontalViewItem label="Роль в PIM">
                            {formatRole(userData?.role_pim_name)}
                        </HorizontalViewItem>
                        <HorizontalViewItem label="Роль в ADMIN">
                            {formatRole(userData?.role_admin_name)}
                        </HorizontalViewItem>
                        <HorizontalViewItem label="Роль в POS">
                            {formatRole(userData?.role_pos_name)}
                        </HorizontalViewItem>
                    </Card>

                </Col>
            </Row>

            <Modal
                title="Пароль пользователя сброшен"
                open={resetPasswordModalVisible}
                onCancel={() => setResetPasswordModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setResetPasswordModalVisible(false)}>
                        Закрыть
                    </Button>
                ]}
            >
                {resetPasswordData && (
                    <>
                        <p>Отправьте следующие данные пользователю:</p>

                        <div style={{marginBottom: 16}}>
                            <Text strong>Временный пароль:</Text>
                            <div style={{ display: 'flex', marginTop: 8 }}>
                                <Input
                                    readOnly
                                    value={resetPasswordData.temporary_password}
                                    style={{ marginRight: 8 }}
                                />
                                <Tooltip title="Скопировать пароль">
                                    <Button
                                        icon={<CopyOutlined />}
                                        onClick={() => handleCopyText(resetPasswordData.temporary_password)}
                                    />
                                </Tooltip>
                            </div>
                        </div>

                        <div style={{ marginTop: 16 }}>
                            <Text strong>Полные данные для входа:</Text>
                            <div style={{ display: 'flex', marginTop: 8 }}>
                                <TextArea
                                    readOnly
                                    rows={4}
                                    value={getFullCredentialsText()}
                                    style={{ marginRight: 8 }}
                                />
                                <Tooltip title="Скопировать данные">
                                    <Button
                                        icon={<CopyOutlined />}
                                        onClick={() => handleCopyText(getFullCredentialsText())}
                                    />
                                </Tooltip>
                            </div>
                        </div>
                    </>
                )}
            </Modal>
        </>
    );
};

export default UserDetails;